import React, { useState, useEffect, useMemo, useCallback, useImperativeHandle } from 'react';
import { InputInsert, Insert } from '../../types/mold';
import { useInserts } from '../../hooks/useInserts';
import { validateAllFields, ValidationRule } from '../../utils/validation';
import TableComponent from './TableComponent';
import { getColumnsForInsertList } from '../InsertList/columns';
import ActionButton from './ActionButton';
import { Trash2, Edit, ImagesIcon } from 'lucide-react';
import DeleteConfirmationDialog from '../MoldDetail/DeleteConfirmationDialog';
import SearchInsertModal from '../MoldDetail/SearchInsertModal';
import EditableModal from './EditableModal';
import ErrorNotificationModal from './ErrorNotificationModal';
// Removed AddButton import - using ActionButton instead
import { SearchFieldConfig } from '../../types/mold';
import { styleConfig } from '../../constants/design';
import ImageUploadModal from './ImageUploadModal';

export const makeInsertValidationRules = (allInserts: Insert[], isEditMode: boolean, currentInsertId?: number): { [key: string]: ValidationRule[] } => {
    const uniqueInsertValidation = async (value: string) => {
        // If no data is available yet, consider it unique (validation will run again when data is loaded)
        if (!allInserts || allInserts.length === 0) {
            return true;
        }

        // If in edit mode, exclude the current insert from the uniqueness check
        const insertsToCheck = isEditMode && currentInsertId ?
            allInserts.filter(insert => insert.id !== currentInsertId) :
            allInserts;

        const isUnique = insertsToCheck.every((insert) => insert.name !== value);
        return isUnique;
    };

    return {
        name: [
            {
                type: 'regex',
                regex: /^[a-zA-Z0-9-\s]+$/,
                message: 'ชื่ออินเสิร์ทต้องประกอบด้วยตัวอักษร ตัวเลข หรือช่องว่างเท่านั้น',
            },
            {
                type: 'unique',
                message: 'อินเสิร์ทนี้มีอยู่แล้ว',
                uniqueValidation: uniqueInsertValidation,
            },
        ],
    };
};

export interface InsertManagerProps {
    // For list mode - when showing all inserts from the database
    mode?: 'list' | 'selection';
    // For selection mode - when showing selected inserts (e.g., in mold details)
    inserts?: Insert[];
    onInsertsChange?: (inserts: Insert[]) => void;
    onAddInsert?: (insertId: number) => Promise<void>;
    onRemoveInsert?: (insertId: number) => Promise<void>;
    renderActions?: (insert: Insert) => React.ReactNode;
    showSearchInsertModal?: boolean;
    showAddNewButton?: boolean;
    showActionButtons?: boolean;
    showEditButton?: boolean;
    showDeleteButton?: boolean;
    showImageUploadButton?: boolean;
    modalHeaderPrefix?: string;
    onSuccessMessage?: (message: string) => void;
    onErrorMessage?: (message: string) => void;
    moldId?: string;
    title?: string | React.ReactNode;
    searchConfig?: SearchFieldConfig;
}

export interface InsertManagerRef {
    openCreateModal: () => void;
}

const InsertManager = React.forwardRef<InsertManagerRef, InsertManagerProps>(({
    mode = 'list',
    inserts: externalInserts,
    onInsertsChange,
    onAddInsert,
    onRemoveInsert,
    renderActions,
    showSearchInsertModal = true,
    showAddNewButton = true,
    showActionButtons = true,
    showEditButton = true,
    showDeleteButton = false,
    showImageUploadButton = false,
    modalHeaderPrefix = '',
    onSuccessMessage,
    onErrorMessage,
    moldId,
    title = 'ทะเบียนอินเสิร์ท',
    searchConfig
}, ref) => {
    // Use internal hook for list mode, external props for selection mode
    const {
        inserts: hookInserts,
        loading: hookLoading,
        fetchInserts,
        updateInsert: hookUpdateInsert,
        createInsert: hookCreateInsert
    } = useInserts();
    const [isImagesModalOpen, setIsImagesModalOpen] = useState(false);

    // Choose data source based on mode
    const rawInserts = mode === 'list' ? hookInserts : (externalInserts || []);

    // Apply search filtering
    const inserts = useMemo(() => {
        if (!searchConfig?.value || !searchConfig.value.trim()) {
            return rawInserts;
        }

        const searchLower = searchConfig.value.toLowerCase();
        const searchField = searchConfig.field;

        return rawInserts.filter((insert) => {
            switch (searchField) {
                case 'name':
                    return insert.name.toLowerCase().includes(searchLower);
                case 'id':
                    return String(insert.id).toLowerCase().includes(searchLower);
                case 'all':
                default:
                    return (
                        insert.name.toLowerCase().includes(searchLower) ||
                        String(insert.id).toLowerCase().includes(searchLower)
                    );
            }
        });
    }, [rawInserts, searchConfig?.value, searchConfig?.field]);

    const isLoading = mode === 'list' ? hookLoading : false;

    const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null);
    const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedInsert, setSelectedInsert] = useState<InputInsert | null>(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});

    // Error notification modal state
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorModalMessage, setErrorModalMessage] = useState('');

    // Fetch data on mount for list mode
    useEffect(() => {
        if (mode === 'list') {
            fetchInserts();
        }
    }, [mode, fetchInserts]);

    // Always fetch inserts for validation, regardless of mode
    // This ensures uniqueness validation always has access to all inserts
    useEffect(() => {
        if (mode === 'selection' && hookInserts.length === 0) {
            fetchInserts();
        }
    }, [mode, hookInserts.length, fetchInserts]);

    // Helper function to handle error messages
    const handleError = useCallback((message: string) => {
        if (onErrorMessage) {
            onErrorMessage(message);
        } else {
            console.log('Showing error modal for:', message);
            setErrorModalMessage(message);
            setShowErrorModal(true);
        }
    }, [onErrorMessage]);

    // Use memoization for validation rules to prevent unnecessary recalculations
    // For uniqueness validation, we should check against all inserts in the database, not just the filtered/selected ones
    const validationRules = useMemo(() => {
        return makeInsertValidationRules(hookInserts, isEditMode, selectedInsert?.id);
    }, [hookInserts, isEditMode, selectedInsert?.id]);

    const handleRemoveInsert = async (insertId: number) => {
        try {
            if (mode === 'list') {
                // For list mode, we would need to implement delete from database
                // This is not typically needed for insert management
                throw new Error("Delete from list mode not implemented");
            } else {
                // For selection mode, just remove from the local array
                if (onInsertsChange) {
                    const updatedInserts = inserts.filter(insert => insert.id !== insertId);
                    onInsertsChange(updatedInserts);
                }
                if (onRemoveInsert) {
                    await onRemoveInsert(insertId);
                }
            }
            setShowDeleteConfirm(null);

            if (onSuccessMessage) {
                onSuccessMessage("ลบอินเสิร์ทสำเร็จ");
            }
        } catch (err) {
            handleError("เกิดข้อผิดพลาดในการลบอินเสิร์ท");
        }
    };

    const handleAddInsert = async (insertId: number, insert: Insert) => {
        try {
            if (onAddInsert) {
                await onAddInsert(insertId);
            }

            if (onInsertsChange) {
                const updatedInserts = [...inserts, insert];
                onInsertsChange(updatedInserts);
            }

            setIsSearchModalOpen(false);

            if (onSuccessMessage) {
                onSuccessMessage("เพิ่มอินเสิร์ทสำเร็จ");
            }
        } catch (err) {
            handleError("เกิดข้อผิดพลาดในการเพิ่มอินเสิร์ท");
        }
    };

    const handleCreateClick = () => {
        setSelectedInsert({
            name: '',
            status: 'active',
            quantity: 1,
        });
        setIsEditMode(false);
        setIsModalOpen(true);
    };

    useImperativeHandle(ref, () => ({
        openCreateModal: () => handleCreateClick(),
    }), [/* deps */]);

    const handleEditClick = (insert: Insert) => {
        setSelectedInsert({
            ...insert,
        });
        setIsEditMode(true);
        setIsModalOpen(true);
    };

    const showImagesModal = (insert: Insert) => {
        setIsImagesModalOpen(true);
        setSelectedInsert({
            ...insert,
        });
    }

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedInsert(null);
        setValidationErrors({});
    };

    const handleCloseImagesModal = () => {
        setIsImagesModalOpen(false);
        setSelectedInsert(null);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        if (selectedInsert) {
            setSelectedInsert({ ...selectedInsert, [name]: value } as InputInsert);
        }
        setValidationErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedInsert) return;

        const errors = await validateAllFields(selectedInsert, validationRules);
        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return;
        }

        try {
            if (isEditMode && selectedInsert.id !== undefined) {
                await hookUpdateInsert(selectedInsert.id, {
                    name: selectedInsert.name,
                    status: selectedInsert.status,
                    quantity: selectedInsert.quantity,
                });
                // For selection mode, update in local array
                if (onInsertsChange) {
                    const updatedInserts = inserts.map(insert =>
                        insert.id === selectedInsert.id
                            ? { ...insert, ...selectedInsert }
                            : insert
                    );
                    onInsertsChange(updatedInserts);
                }

                if (onSuccessMessage) {
                    onSuccessMessage("แก้ไขอินเสิร์ทสำเร็จ");
                }
            } else {
                const createdInsert = await hookCreateInsert({
                    name: selectedInsert.name,
                    status: selectedInsert.status,
                    quantity: selectedInsert.quantity,
                });

                // For selection mode, add to local array
                if (onInsertsChange && createdInsert) {
                    onInsertsChange([...inserts, createdInsert]);
                }

                if (onAddInsert) {
                    await onAddInsert(createdInsert.id);
                }

                if (onSuccessMessage) {
                    onSuccessMessage("สร้างอินเสิร์ทใหม่สำเร็จ");
                }
            }
            handleCloseModal();
        } catch (error) {
            console.error('Failed to save insert:', error);
            handleError("เกิดข้อผิดพลาดในการบันทึกข้อมูล: " + (error instanceof Error ? error.message : String(error)));
        }
    };

    // Default custom render actions to match MoldDetail functionality
    const defaultRenderActions = (insert: Insert) => (
        <div className="flex space-x-2">
            {showImageUploadButton && <ActionButton
                onClick={() => showImagesModal(insert)}
                color="outline-primary"
                size="sm"
            >
                <ImagesIcon size={18} />
            </ActionButton>}
            {showEditButton && <ActionButton
                onClick={() => handleEditClick(insert)}
                color="outline-primary"
                size="sm"
            >
                <Edit size={18} />
            </ActionButton>}
            {showDeleteButton && <ActionButton
                onClick={() => setShowDeleteConfirm(insert.id)}
                color="outline-danger"
                size="sm"
            >
                <Trash2 size={18} />
            </ActionButton>}
        </div>
    );

    // Use custom renderActions if provided, otherwise use default
    const actualRenderActions = renderActions || defaultRenderActions;

    return (
        <div>
            {/* Header with Add Button */}
            {(showAddNewButton || showSearchInsertModal) && (
                <div className="mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-1 sm:gap-0">
                    <h1 className="text-base sm:text-2xl font-bold pt-1 sm:mb-0">
                        {title}
                    </h1>
                    {showActionButtons && <div className="flex space-x-2">
                        {showSearchInsertModal && mode === 'selection' && (
                            <ActionButton
                                onClick={() => setIsSearchModalOpen(true)}
                                color="outline-primary"
                                size="md"
                                className="rounded-full shadow-sm"
                                testIdIdentifier="search-insert-button"
                            >
                                เพิ่มอินเสิร์ทที่มีอยู่แล้ว
                            </ActionButton>
                        )}
                        {showAddNewButton && (
                            <ActionButton
                                onClick={handleCreateClick}
                                color="primary"
                                size="md"
                                className="rounded-full shadow-sm"
                                testIdIdentifier="new-insert-button"
                            >
                                สร้างอินเสิร์ทใหม่
                            </ActionButton>
                        )}
                    </div>}
                </div>
            )}

            {isLoading && (
                <div className="text-center py-4">Loading...</div>
            )}

            {!isLoading && (
                <>
                    {inserts.length > 0 ? (
                        <div className="overflow-x-auto">
                            <TableComponent<Insert>
                                data={inserts}
                                columns={[
                                    ...getColumnsForInsertList({ includeMold: mode === 'list' }),
                                    ...(showActionButtons ? [{
                                        key: 'actions',
                                        label: 'Actions',
                                        sortable: false,
                                        filterable: false,
                                        render: actualRenderActions
                                    }] : [])
                                ]}
                                savePreferenceSettings={{
                                    key: mode === 'list' ? "insertList" : "mold_insertList",
                                    isEnable: true
                                }}
                                pagination={true}
                                defaultSortKey="id"
                            />
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-lg border border-gray-200 text-center">
                            <div className="bg-blue-50 p-6 rounded-full mb-4 shadow-sm">
                                <styleConfig.insert.icon size={40} className="text-blue-500" />
                            </div>
                            <p className="text-lg font-medium text-gray-700 mb-2">{mode === 'selection' ? 'ไม่มีอินเสิร์ทที่เลือก' : 'ไม่พบอินเสิร์ท'}</p>
                            {isEditMode && <p className="text-sm text-gray-500 max-w-md">คุณสามารถเพิ่มอินเสิร์ทใหม่หรือเลือกจากอินเสิร์ทที่มีอยู่แล้วได้โดยใช้ปุ่มด้านบน</p>}
                        </div>
                    )}
                </>
            )}

            {showDeleteConfirm !== null && (
                <DeleteConfirmationDialog
                    setShowDeleteConfirm={setShowDeleteConfirm}
                    text={`คุณแน่ใจหรือไม่ว่าต้องการลบอินเสิร์ทนี้${moldId ? `ออกจากแม่พิมพ์รหัส ${moldId}` : ''}?`}
                    onConfirm={() => handleRemoveInsert(showDeleteConfirm)}
                />
            )}

            {isSearchModalOpen && showSearchInsertModal && (
                <SearchInsertModal
                    onClose={() => setIsSearchModalOpen(false)}
                    onAddInsert={handleAddInsert}
                    associatedInsertIds={inserts.map((insert) => insert.id)}
                />
            )}

            <EditableModal<InputInsert>
                header={`${modalHeaderPrefix}${isEditMode ? 'แก้ไขอินเสิร์ท' : 'เพิ่มอินเสิร์ทใหม่'}`}
                isOpen={isModalOpen}
                isEditMode={isEditMode}
                item={selectedInsert}
                fields={[
                    {
                        key: 'name',
                        label: 'ชื่ออินเสิร์ท',
                        type: 'text',
                        required: true,
                        placeholder: 'พิมพ์ชื่ออินเสิร์ท',
                    },
                    {
                        key: 'status',
                        type: 'select',
                        label: 'สถานะ',
                        required: true,
                        options: [
                            { value: 'active', label: 'ใช้งาน' },
                            { value: 'in_used', label: 'กำลังใช้งาน' },
                            { value: 'cancelled', label: 'ยกเลิก' },
                        ]
                    },
                    {
                        key: 'quantity',
                        label: 'จำนวน',
                        type: 'number',
                        required: true,
                        placeholder: 'พิมพ์จำนวน',
                    },
                ]}
                onClose={handleCloseModal}
                onSubmit={handleSubmit}
                onInputChange={handleInputChange}
                validationErrors={validationErrors}
            />

            <ErrorNotificationModal
                isOpen={showErrorModal}
                onClose={() => setShowErrorModal(false)}
                errorMessage={errorModalMessage}
            />

            {isImagesModalOpen && (
                <ImageUploadModal
                    onClose={handleCloseImagesModal}
                    selectedInsert={selectedInsert}
                />
            )}
        </div>
    );
});

export default InsertManager;
